import type { NgwWebmapItem, TreeItem } from "@nextgis/ngw-kit";

export interface LayerTreeOptions {
  hiddenLayerIds?: readonly number[];
}

export function setLeafVisibility(
  layer: NgwWebmapItem,
  visible: boolean,
  options: LayerTreeOptions = {},
): void {
  hideExclusiveSiblings(layer, visible, options);
  layer.properties?.property("visibility")?.set(visible);
}

export function setBranchVisibility(
  layer: NgwWebmapItem,
  visible: boolean,
  options: LayerTreeOptions = {},
): void {
  const item = layer.item;

  if (item.item_type === "layer") {
    if (!isHiddenLayer(item, options)) {
      setLeafVisibility(layer, visible, options);
    }
    return;
  }

  hideExclusiveSiblings(layer, visible, options);

  const children = layer.tree
    .getChildren()
    .toReversed()
    .filter((child) => !isHiddenLayer(child.item, options));

  if (!children.length) return;

  if (visible && item.item_type === "group" && item.group_exclusive) {
    children.forEach((child, index) => {
      setBranchVisibility(child, index === 0, options);
    });
    return;
  }

  children.forEach((child) => setBranchVisibility(child, visible, options));
}

export function getBranchIndeterminate(
  layer: NgwWebmapItem,
  options: LayerTreeOptions = {},
): boolean {
  if (layer.item?.item_type === "layer") return false;
  const leaves = getVisiblePanelLeaves(layer, options);
  const visibleCount = leaves.filter((leaf) =>
    getLayerVisible(leaf, options),
  ).length;
  return visibleCount > 0 && visibleCount < leaves.length;
}

export function getLayerVisible(
  layer: NgwWebmapItem,
  options: LayerTreeOptions = {},
): boolean {
  if (layer.item?.item_type !== "layer") {
    return getVisiblePanelLeaves(layer, options).some((leaf) =>
      getLeafVisible(leaf, options),
    );
  }
  return getLeafVisible(layer, options);
}

function getLeafVisible(
  layer: NgwWebmapItem,
  options: LayerTreeOptions,
): boolean {
  if (layer.item?.item_type !== "layer" || isHiddenLayer(layer.item, options)) {
    return false;
  }
  return layer.properties?.property("visibility")?.get() === true;
}

export function getLayerTitle(item: TreeItem, fallback: string): string {
  return item.display_name || fallback;
}

export function isHiddenLayer(
  item: TreeItem,
  { hiddenLayerIds = [] }: LayerTreeOptions = {},
): boolean {
  if (item.item_type !== "layer") return false;

  const ids = [item.layer_style_id, item.style_parent_id, item.resourceId]
    .filter((value) => value !== undefined && value !== null)
    .map(Number);
  return ids.some((id) => hiddenLayerIds.includes(id));
}

function hideExclusiveSiblings(
  layer: NgwWebmapItem,
  visible: boolean,
  options: LayerTreeOptions,
): void {
  if (!visible) return;

  let current = layer;
  let parent = current.tree.getParent();

  while (parent) {
    if (parent.item?.item_type === "group" && parent.item.group_exclusive) {
      parent.tree.getChildren().forEach((sibling) => {
        if (sibling !== current && !isHiddenLayer(sibling.item, options)) {
          setBranchVisibility(sibling, false, options);
        }
      });
    }

    current = parent;
    parent = current.tree.getParent();
  }
}

function getVisiblePanelLeaves(
  layer: NgwWebmapItem,
  options: LayerTreeOptions,
): NgwWebmapItem[] {
  if (layer.item?.item_type === "layer") {
    return isHiddenLayer(layer.item, options) ? [] : [layer];
  }
  return layer.tree
    .getChildren()
    .flatMap((child) => getVisiblePanelLeaves(child, options));
}
